import React from 'react';
import { useUserProfileRedux } from '@root/hooks/useUserProfileRedux';
import { useAppSelector } from '@root/app/store/hooks';
import { CircularProgress, Box } from '@mui/material';
import AccessDenied from '@root/shared/components/AccessDenied';
import { SessionStorageService } from '@root/shared/services/SessionStorageService';

interface AccessControlWrapperProps {
  children: React.ReactNode;
}

export const AccessControlWrapper: React.FC<AccessControlWrapperProps> = ({ children }) => {
  const { userProfile, loading, error } = useUserProfileRedux();
  const consecutiveFailures = useAppSelector((state) => state.user.consecutiveFailures);
  
  // Get user email from session storage
  const userEmail = typeof window !== 'undefined' ? sessionStorage.getItem('userEmail') : null;
  
  // Check if user has a token (authentication completed)
  const hasToken = SessionStorageService.hasToken();
  
  // If no token yet, don't show loading or access denied - ProtectedRoute will handle redirect
  if (!hasToken) {
    return <>{children}</>;
  }
  
  // If we have a valid user profile, grant access immediately
  if (userProfile && userProfile.email) {
    return <>{children}</>;
  }
  
  // If we've exceeded max failures, show access denied with helpful message
  if (consecutiveFailures >= 3 && !userProfile) {
    return <AccessDenied 
      userEmail={userEmail || undefined} 
      message="Unable to load your profile due to network issues. Please refresh the page or contact support."
      showRetry={true}
    />;
  }
  
  // Show loading while fetching profile (only if we have a token and haven't exceeded max attempts)
  if (loading && hasToken && consecutiveFailures < 3) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  // Determine if access is denied (has token, finished loading, no profile, and has error)
  const accessDenied = hasToken && !loading && !userProfile && error && consecutiveFailures > 0;

  // Show access denied page if user doesn't have access
  if (accessDenied) {
    return <AccessDenied userEmail={userEmail || undefined} />;
  }

  // Default: render children (this handles the initial loading state before profile fetch)
  return <>{children}</>;
};

export default AccessControlWrapper;
